import React from "react";
import { Card, CardContent, Typography, Box, TextField, Grid } from "@mui/material";
import { Save } from "@mui/icons-material";

const MapPositionSettings = ({
  item,
  setItem,
  handleSave,
  buttonColor,
}) => {
  // Default map position settings - width/height in pixels, offsets in percentage
  const defaultMapPosition = {
    widthPx: 537,    // ~28% of 1920px
    heightPx: 1080,  // ~75% of 1440px (assuming 1920x1080 or similar)
    top: 10,         // 10%
    right: 3,        // 3%
    left: null,
    bottom: null,
  };

  const mapPosition = item?.mapPosition || defaultMapPosition;

  const handleMapPositionChange = (field, value) => {
    // Parse the value as a number, or set to null if empty
    const numValue = value === "" || value === null ? null : parseFloat(value);

    setItem((prev) => ({
      ...prev,
      mapPosition: {
        ...defaultMapPosition,
        ...(prev.mapPosition || {}),
        [field]: numValue,
      },
    }));
  };

  // Helper function to get value with proper fallback - handles 0 correctly
  const getValue = (field, defaultValue) => {
    const value = mapPosition?.[field];
    // Return the value if it's a number (including 0) or explicitly null/undefined
    if (typeof value === 'number') {
      return value;
    }
    // For null/undefined, return default
    return defaultValue;
  };

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Map Position & Size Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure the position and size of the map overlay on the Avatar Screen
        </Typography>

        <Grid container spacing={3}>
          {/* Map Width */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Map Width (px)"
              type="number"
              value={getValue('widthPx', 537)}
              onChange={(e) => handleMapPositionChange("widthPx", e.target.value)}
              inputProps={{ min: 192, max: 960, step: 1 }}
              helperText="Range: 192px - 960px (10% - 50% of 1920px)"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: buttonColor,
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: buttonColor,
                },
              }}
            />
          </Grid>

          {/* Map Height */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Map Height (px)"
              type="number"
              value={getValue('heightPx', 1080)}
              onChange={(e) => handleMapPositionChange("heightPx", e.target.value)}
              inputProps={{ min: 324, max: 972, step: 1 }}
              helperText="Range: 324px - 972px (30% - 90% of 1080px)"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: buttonColor,
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: buttonColor,
                },
              }}
            />
          </Grid>

          {/* Top Offset (Optional) */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Top Offset (Optional) (%)"
              type="number"
              value={typeof mapPosition?.top === 'number' ? mapPosition.top : ""}
              onChange={(e) => handleMapPositionChange("top", e.target.value)}
              inputProps={{ min: 0, max: 50, step: 0.5 }}
              helperText="Leave empty to unset. Range: 0% - 50%"
              placeholder="Not Set"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: buttonColor,
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: buttonColor,
                },
              }}
            />
          </Grid>

          {/* Right Offset (Optional) */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Right Offset (Optional) (%)"
              type="number"
              value={typeof mapPosition?.right === 'number' ? mapPosition.right : ""}
              onChange={(e) => handleMapPositionChange("right", e.target.value)}
              inputProps={{ min: 0, max: 20, step: 0.5 }}
              helperText="Leave empty to unset. Range: 0% - 20%"
              placeholder="Not Set"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: buttonColor,
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: buttonColor,
                },
              }}
            />
          </Grid>

          {/* Left Offset (Optional) */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Left Offset (Optional) (%)"
              type="number"
              value={typeof mapPosition?.left === 'number' ? mapPosition.left : ""}
              onChange={(e) => handleMapPositionChange("left", e.target.value)}
              inputProps={{ min: 0, max: 20, step: 0.5 }}
              helperText="Leave empty to use Right Offset. Range: 0% - 20%"
              placeholder="Not Set"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: buttonColor,
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: buttonColor,
                },
              }}
            />
          </Grid>

          {/* Bottom Offset (Optional) */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Bottom Offset (Optional) (%)"
              type="number"
              value={typeof mapPosition?.bottom === 'number' ? mapPosition.bottom : ""}
              onChange={(e) => handleMapPositionChange("bottom", e.target.value)}
              inputProps={{ min: 0, max: 50, step: 0.5 }}
              helperText="Leave empty to use Top Offset. Range: 0% - 50%"
              placeholder="Not Set"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: buttonColor,
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: buttonColor,
                },
              }}
            />
          </Grid>
        </Grid>



        {/* Save Button */}
        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={handleSave}
            style={{
              backgroundColor: buttonColor,
              color: "#FFFFFF",
              border: "none",
              borderRadius: "5px",
              padding: "10px 20px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Save />
            Save Map Position Settings
          </button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MapPositionSettings;
